import { useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UseFormReturn } from "react-hook-form";
import { useTenants } from "@/hooks/useTenants";
import { useSingleTenant } from "@/hooks/useSingleTenant";

interface BasicInfoStepProps {
  form: UseFormReturn<any>;
}

export const BasicInfoStep = ({ form }: BasicInfoStepProps) => {
  const { tenants, isLoading } = useTenants();
  const customers = (tenants || []).map(t => ({ id: t.id, name: t.name }));
  const singleTenant = useSingleTenant();

  // Auto-assign the first customer when single-tenant
  useEffect(() => {
    if (singleTenant && customers.length > 0 && !form.getValues("customer_id")) {
      form.setValue("customer_id", customers[0].id);
    }
  }, [singleTenant, customers.length]);
  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Basic Information</h2>
        <p className="text-muted-foreground">
          Give your enrollment profile a name and description
        </p>
      </div>

      <div className="space-y-6">
        {!singleTenant && (
          <FormField
            control={form.control}
            name="customer_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Customer *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a customer" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="profile_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Profile Name *</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Enter enrollment profile name" 
                  {...field}
                  className="text-lg"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Describe the purpose of this enrollment profile..."
                  rows={4}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="department"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Department</FormLabel>
              <FormControl>
                <Input 
                  placeholder="e.g., IT, Sales, Marketing" 
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};
